import axios, { AxiosError, AxiosRequestConfig } from "axios";
import AppConfig from "../config/AppConfig";
import BaseResponse from "../model/base/BaseResponse";

class ApiClientClass {
  private static apiClient: ApiClientClass | undefined = undefined;
  private axiosClient = axios.create({
    baseURL: AppConfig.baseURL,
    responseType: "json",
    timeout: 30000,
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
    }
  });
  
  private constructor() {
    this.setupAccessTokenInterceptor();
    this.logoutWhenUnAuth();
  }
  
  public static getInstance(): ApiClientClass {
    if (!this.apiClient) {
      this.apiClient = new ApiClientClass();
    }
    return this.apiClient;
  }
  
  private setupAccessTokenInterceptor() {
    this.axiosClient.interceptors.request.use(
      (config) => {
        try {
          const root = localStorage.getItem("user_data");

          if (root) {
            const parsedRoot = JSON.parse(root);
            const token = parsedRoot.access_token;

            if (token && config.headers) {
              config.headers["Authorization"] = `Bearer ${token}`;
            }
          }
        } catch (err) {
          console.warn("Failed to parse token from localStorage", err);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  private logoutWhenUnAuth() {
    // Interceptor cho response: Tự động logout nếu gặp 401
    this.axiosClient.interceptors.response.use(
      (response) => {
         if (response.data?.code === 401) {
          console.warn("Backend trả về code 401 trong response body. Logging out...");
          localStorage.removeItem("user_data");
          window.location.href = "/login";
          return Promise.reject({
            message: 'Unauthorized from response body',
            response,
          });
        }

        return response;
      },
      (error) => {
        if (error.response && error.response.status === 401) {
          console.warn("Unauthorized. Logging out...");

          // Xóa dữ liệu user
          localStorage.removeItem("user_data");

          // Redirect đến trang login
          window.location.href = "/login";
        }

        return Promise.reject(error);
      }
    );
  }


  public updateAccessToken(token: string | undefined) {
    this.axiosClient.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token}`;
  }

  async requestIgnoreException<T, Data>(config: AxiosRequestConfig<Data>): Promise<BaseResponse<T>>{
    const newConfig:AxiosRequestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "X-Client-Call-Time": (Date.now())
      }
    }
    return await axios.get(AppConfig.baseURL + newConfig.url!).then(res => {
      return res.data;
    }).catch(error => {
      return error.response.data;
    })
  }
  
  async request<T, Data>(config: AxiosRequestConfig<Data>): Promise<BaseResponse<T>> {
    try {
      const newConfig:AxiosRequestConfig = {
        ...config,
        headers: {
          ...config.headers,
          "X-Client-Call-Time": (Date.now())
        }
      }
      return (await this.axiosClient.request<BaseResponse<T>>(newConfig)).data;
    } catch (e) {
      if (e instanceof AxiosError) {
        return {
          code: e.response?.data?.code || 600,
          message: e.message
        };
      }
      return {
        code: 600,
        message: "An error occurred"
      };
    }
  }
  
  post<T, Data = any>(url: string, data?: Data, config?: AxiosRequestConfig<Data>): Promise<BaseResponse<T>> {
    
    return this.request({
      ...config,
      url,
      data,
      method: "POST"
    });
  }
  put<T, Data = any>(url: string, data?: Data, config?: AxiosRequestConfig<Data>): Promise<BaseResponse<T>> {
    
    return this.request({
      ...config,
      url,
      data,
      method: "PUT"
    });
  }
  delete<T, Data = any>(url: string, data?: Data, config?: AxiosRequestConfig<Data>): Promise<BaseResponse<T>> {
    return this.request({
      ...config,
      url,
      data,
      method: "DELETE"
    });
  }
  get<T>(url: string, config?: AxiosRequestConfig): Promise<BaseResponse<T>> {
    return this.request({
      ...config,
      url,
      method: "GET"
    });
  }

  getIgnoreException<T>(url: string, config?: AxiosRequestConfig): Promise<BaseResponse<T>> {
    return this.requestIgnoreException({
      ...config,
      url,
      method: "GET"
    });
  }
  
  patch<T, Data = any>(url: string, data?: Data, config?: AxiosRequestConfig<Data>): Promise<BaseResponse<T>> {
    return this.request({
      ...config,
      url,
      data,
      method: "PATCH"
    });
  }
  
}

export const ApiClient = ApiClientClass.getInstance();